import React, { createContext, useContext, useMemo } from 'react'
import { VersionFragment } from '@sketch/gql-types'
import type { BaseResponsiveDropdownProps } from '@sketch/components'

/**
 * The few things the inspector needs from the application around it.
 *
 * Everything else in this package is self-contained, but a couple of leaves
 * need to reach the host: a colour variable wants to know whether the
 * document's components are still being ingested, and the symbol path wants
 * the host's navigation dropdown, which knows about routes and versions.
 *
 * All of it is optional. Without a provider the inspector renders the same
 * way, minus those two affordances, which keeps it usable in tests and in
 * Storybook.
 */
export interface InspectorHost {
  /**
   * True while the document's components are still being processed, in which
   * case colour variable details are not available yet and a placeholder is
   * shown instead.
   */
  isIngestingComponents: boolean
  /**
   * The host's dropdown for navigating a symbol's path. Rendered by
   * `SymbolPath`; without it the path is shown as plain text.
   */
  symbolPathDropdown?: React.ComponentType<
    { paths: string[] } & BaseResponsiveDropdownProps
  >
  /**
   * Links an inspected component to where it lives in the host application —
   * a colour variable to its document's colour variables page, for instance.
   *
   * Building that link needs the host's routes and its notion of the current
   * version, neither of which this package knows about. Without one the name
   * is shown as plain text.
   */
  componentLink?: React.ComponentType<InspectorComponentLinkProps>
  /**
   * The document being inspected. Needed to look up the components a colour
   * variable or shared style points at, which is a versioned query.
   */
  document?: {
    shareIdentifier: string
    currentVersion?: VersionFragment
  }
}

export interface InspectorComponentLinkProps {
  /** True when the component comes from another document (a library). */
  isForeign: boolean
  /** The share the component belongs to, when it is foreign. */
  shareID?: string
  /** Query string to carry through to the linked page. */
  search?: string
  children: React.ReactNode
}

const DEFAULT_HOST: InspectorHost = { isIngestingComponents: false }

const inspectorHostContext = createContext<InspectorHost>(DEFAULT_HOST)

export function InspectorHostProvider({
  isIngestingComponents,
  symbolPathDropdown,
  componentLink,
  document,
  children,
}: InspectorHost & { children: React.ReactNode }) {
  const value = useMemo(
    () => ({
      isIngestingComponents,
      symbolPathDropdown,
      componentLink,
      document,
    }),
    [isIngestingComponents, symbolPathDropdown, componentLink, document]
  )

  return (
    <inspectorHostContext.Provider value={value}>
      {children}
    </inspectorHostContext.Provider>
  )
}

export function useInspectorHost(): InspectorHost {
  return useContext(inspectorHostContext)
}
